import { MessageOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Badge, Button, Card, message, Popconfirm, Skeleton, Space, Typography } from 'antd';
import { postchallengeParticipate } from 'api/services/challenges/challenges.services';
import { formatCreditDate, StatusCode, UserRoles } from 'common/enums';
import * as appRoutes from "config/routes.config";
import { badRequest } from 'messages';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Avatar1 from "../../../assets/images/app/avatars/avatar_1.svg";
import Avatar3 from "../../../assets/images/app/avatars/avatar_3.svg";
import Avatar4 from "../../../assets/images/app/avatars/avatar_4.svg";
import Avatar6 from "../../../assets/images/app/avatars/avatar_6.svg";
import EmptyIcon from "../../../assets/images/app/empty_icon.svg";
import OrganismIcon from "../../../assets/images/app/iam_organism.svg";
import Students from "../../../assets/images/app/iam_student.svg";
import MessageIcon from "../../../assets/images/app/message_icon.png";
import SendIcon from "../../../assets/images/app/send-message.png";
import CountdownTimer from '../CountdownTimer/CountdownTimer';
import './Sidebar.css';

const MESSAGE_PREFIX_PARTAGE_RESEAUX_SOCIAUX =
  "Bonjour ! Rejoins ma classe sur Monamilabert.com en utilisant ce code :";

const { Text, Title } = Typography;

interface SidebarProps {
  data: any;
  challenge: any;
  onSendMessage?: () => void;
  onSendCollectifMessage?: () => void;
  onSendOrganismMessage?: () => void;
  onAddTeacher?: () => void;
  onStartChallenge?: () => void;
  onStopChallenge?: (challengeId: string) => void;
  onShowStudents?: () => void;
  onInviteStudents?: () => void;
  onSeeCalendar?: () => void;
}

const ClassroomSidebar: React.FC<SidebarProps> = ({
  data,
  challenge,
  onSendMessage,
  onSendCollectifMessage,
  onSendOrganismMessage,
  onAddTeacher,
  onStartChallenge,
  onStopChallenge,
  onShowStudents,
  onInviteStudents,
  onSeeCalendar
}) => {

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isTeacher = user?.roles?.includes(UserRoles?.TEACHER);
  const isOrganism = user?.roles?.includes(UserRoles?.ORGANISME);
  const isStudent = user?.roles?.includes(UserRoles?.STUDENT);

  const participants = useMemo(
    () =>
      challenge?.participants?.map((e: any) => ({
        _id: e._id,
        profile: e.profile || e.avatar,
        firstname: e.firstname,
        lastname: e.lastname,
      })),
    [challenge]
  );

  const idsParticipants = useMemo(
    () => participants?.map((ec: any) => ec._id),
    [participants]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [visibleSocialMedia, setVisibleSocialMedia] = useState(false);
  const handleVisibleChange = () => {
    setVisibleSocialMedia(!visibleSocialMedia);
  };
  const hide = () => setVisibleSocialMedia(false);

  const [popconfirmVisible, setPopconfirmVisible] = useState(false);

  const handleConfirm = () => {
    setIsLoading(true);
    setPopconfirmVisible(false);
    onStopChallenge!(challenge?._id);
  }

  const handleCancel = () => {
    setPopconfirmVisible(false);
  }

  const handleCopy = () => {
    message.success("copié dans le presse-papiers");
    hide();
  }

  const StudentChallenge = useCallback(async () => {
    try {
      if (user?._id && challenge?._id) {
        const response = await postchallengeParticipate(user?._id, challenge?._id);
        if (response?.statusCode === StatusCode.CREATED) {
          navigate(
            `${appRoutes.STUDENT_MY_CLASS}/${id}/challenge/${response?.data?._id}`
          );
        }
      }
    } catch (error) {
      message.error(badRequest.error);
    }
  }, [user?._id, challenge, navigate, id]);

  return (
    data ? <div className="sidebar-container">
      {(isTeacher || isStudent) && data?.isCollective && data?.organism && <Card className="organisme-card">
        <img src={OrganismIcon} height={100} />
        <div className="organisme-card-title">{data?.organism?.organisationName}</div>
        {data?.status === "PUBLISHED" && <Button
          type="primary"
          icon={<MessageOutlined />}
          onClick={onSendOrganismMessage}
          className="classBtn classBtn-teal"
          block
          style={{ marginTop: 16, width: "220px" }}
        >
          Envoyer un message
        </Button>}
      </Card>}

      {(isOrganism || isStudent) && data?.teacher ? <Card className="professor-card">
        <div className="professor-info">
          <Avatar size={80} src={data?.teacher?.profile} style={{ borderRadius: "50%" }} icon={<UserOutlined />} />
          <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
            {data?.teacher?.firstname + " " + data?.teacher?.lastname}
          </Title>
          {isOrganism && <Badge
            count={`${data?.teacherCreditsSpent} crédits`}
            style={{
              backgroundColor: '#FFF',
              color: '#5a9e9c',
              fontWeight: 500,
              border: "1px solid #5a9e9c",
              textAlign: "center",
              borderRadius: "8px",
              height: "23px"
            }}
          />}

          {isOrganism && <Space direction="vertical" size={0} style={{ marginTop: 8, width: '100%' }}>
            {data?.teacherCreditsCost && <Text type="secondary" style={{ fontSize: 12, fontWeight: "500" }}>
              Tarif par crédit : <span style={{ color: "#81b1b2" }}>{data?.teacherCreditsCost} €</span>
            </Text>}
            {data?.teacherCreditsExpiry && isOrganism && <Text type="secondary" style={{ fontSize: 12, fontWeight: "500" }}>
              Expiration des crédits : <span style={{ color: "#81b1b2" }}>{formatCreditDate(data?.teacherCreditsExpiry.split("T")[0])}</span>
            </Text>}
          </Space>}

          {data?.status === "PUBLISHED" && <Button
            type="primary"
            icon={<MessageOutlined />}
            onClick={onSendMessage}
            className="classBtn classBtn-teal"
            block
            style={{ marginTop: 16, width: "220px" }}
          >
            Envoyer un message
          </Button>}

          {isOrganism && data?.status === "PUBLISHED" && <Button
            type="primary"
            icon={<MessageOutlined />}
            onClick={onSeeCalendar}
            className="classBtn classBtn-green"
            block
            style={{ marginTop: 16, width: "220px" }}
          >
            Voir le calendrier
          </Button>}
        </div>
      </Card> : isOrganism && data?.status === "PUBLISHED" && <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={onAddTeacher}
        className="classBtn classBtn-teal"
        block
        style={{ width: "250px", margin: "0px auto" }}
      >
        Assigner un prof à cette classe
      </Button>}

      {(isOrganism || isStudent) && <Card className="challenge-card">
        {challenge ? <Text style={{ fontSize: 12, fontWeight: 500 }}>
          Le challenge se termine dans :
        </Text> : <Text style={{ fontSize: 12 }}>
          Aucun challenge pour le moment
        </Text>}
        {challenge ? <CountdownTimer endDate={challenge?.expiresAt} /> : <img src={EmptyIcon} height={70} style={{ marginTop: "20px" }} />}

        {isStudent && challenge && idsParticipants && !idsParticipants.includes(user?._id) ? <Button
          type="primary"
          className="classBtn classBtn-green"
          block
          style={{ width: "auto", margin: "10px auto" }}
          onClick={StudentChallenge}
        >
          Participer au challenge
        </Button> : isStudent && challenge && idsParticipants && idsParticipants.includes(user?._id) && <Button
          type="primary"
          className="classBtn classBtn-teal"
          block
          style={{ width: "auto", margin: "10px auto" }}
        >
          Tu as déjà participé
        </Button>}
      </Card>}

      {isTeacher && <Card className="challenge-card">
        {!challenge && data?.status === "PUBLISHED" && <Button
          type="primary"
          onClick={onStartChallenge}
          className="classBtn classBtn-teal"
          block
          style={{ width: "auto", margin: "0px auto" }}
        >
          + Challenger ma classe
        </Button>}

        {challenge ? <Text style={{ fontSize: 12, marginTop: "15px", fontWeight: 500 }}>
          Le challenge se termine dans :
        </Text> : <Text style={{ fontSize: 12, marginTop: "30px" }}>
          Aucun challenge planifié
        </Text>}

        {challenge ? <CountdownTimer endDate={challenge?.expiresAt} /> : <img src={EmptyIcon} height={70} style={{ marginTop: "20px" }} />}

        {challenge && <span style={{ marginTop: "10px" }}>Inscription : {challenge?.participants?.length} {challenge?.participants?.length > 1 ? "élèves" : "élève"}</span>}

        {challenge && data?.status === "PUBLISHED" && <Popconfirm
          title={`Veux-tu arrêter ce challenge?`}
          visible={popconfirmVisible}
          onVisibleChange={(visible) => {
            if (!isLoading) {
              setPopconfirmVisible(visible);
            }
          }}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          okText="Oui"
          cancelText="Non"
        >
          <Button
            type="primary"
            className="classBtn classBtn-danger"
            block
            style={{ width: "auto", margin: "10px auto" }}
          >
            Arrêter le challenge
          </Button>
        </Popconfirm>}
      </Card>}

      <Card className="students-card">
        <div style={{ width: '100%', display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start" }}>
          <div className="students-card-title">
            <Space onClick={(isTeacher) && data?.registeredStudents.length > 0 && onShowStudents}>
              <img src={Students} height={30} />
              <Text>{isStudent ? "Mes camarades" : "Mes élèves"} ({data?.registeredStudents.length})</Text>
            </Space>

            {isTeacher && !data?.organism && data?.status === "PUBLISHED" && <Button
              className="classBtn classBtn-info"
              block
              style={{ width: "auto", fontSize: "0.85em" }}
              onClick={onInviteStudents}
            >
              Inviter
              <img src={SendIcon} />
            </Button>}
          </div>

          <div className="students-avatars">
            <img src={Avatar1} height={35} style={{ position: "relative", top: "-15px", left: "70px" }} />
            <img src={Avatar3} height={35} style={{ position: "relative", top: "30px", left: "-65px" }} />
            <img src={Avatar4} height={35} style={{ position: "relative", top: "65px", right: "-30px" }} />
            <img src={Avatar6} height={35} style={{ position: "relative", bottom: "-40px", right: "15px" }} />
            <img src={MessageIcon} height={30} style={{ position: "relative", top: "-15%", left: "-15%" }} />
          </div>
          {isOrganism && data?.status === "PUBLISHED" ? <Text style={{ fontSize: 12, textAlign: 'center', fontWeight: 400 }}>
            Échangez régulièrement avec<br />l'ensemble des élèves de votre classe!
          </Text> : isTeacher ? <Text style={{ fontSize: 12, textAlign: 'center', fontWeight: 400 }}>
            Échange régulièrement<br />avec tes élèves.
          </Text> : <Text style={{ fontSize: 12, textAlign: 'center', fontWeight: 400 }}>
            Échange régulièrement avec<br />l'ensemble de tes camarades de classe!
          </Text>}
          {data?.registeredStudents.length > 0 && data?.status === "PUBLISHED" && <Button
            type="primary"
            icon={<MessageOutlined />}
            onClick={onSendCollectifMessage}
            className="classBtn classBtn-teal"
            block
            style={{ marginTop: 16, width: "220px" }}
          >
            Envoyer un message
          </Button>}
        </div>
      </Card>
    </div> : <Skeleton />);
};

export default ClassroomSidebar;
